import { getMsalInstance, pickAccount } from './msal';

/** Microsoft Graph scopes for read-only profile + photo. */
const GRAPH_SCOPES = ['User.Read'];
const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';

/** Subset of the Graph `/me` response we surface as read-only profile info. */
export interface GraphProfile {
  displayName: string | null;
  mail: string | null;
  userPrincipalName: string | null;
  jobTitle: string | null;
  officeLocation: string | null;
  mobilePhone: string | null;
}

/**
 * Acquire a Microsoft Graph access token (separate scope from the API token).
 * Returns null when there is no signed-in MSAL account or acquisition fails
 * (e.g. dev-auth mode, or consent not yet granted) — callers fall back.
 */
async function acquireGraphToken(): Promise<string | null> {
  const msal = getMsalInstance();
  // Same account choice as the token provider, or this reads a profile from the retired tenant.
  const account = pickAccount(msal);
  if (!account) return null;
  try {
    const result = await msal.acquireTokenSilent({ scopes: GRAPH_SCOPES, account });
    return result.accessToken;
  } catch {
    return null;
  }
}

/**
 * Fetch the signed-in user's Microsoft 365 profile via Graph. Returns null if
 * no token can be acquired or the request fails — never throws.
 */
export async function fetchGraphProfile(signal?: AbortSignal): Promise<GraphProfile | null> {
  const token = await acquireGraphToken();
  if (!token) return null;
  try {
    const res = await fetch(
      `${GRAPH_BASE}/me?$select=displayName,mail,userPrincipalName,jobTitle,officeLocation,mobilePhone`,
      { headers: { Authorization: `Bearer ${token}` }, signal },
    );
    if (!res.ok) return null;
    const data = (await res.json()) as Partial<GraphProfile>;
    return {
      displayName: data.displayName ?? null,
      mail: data.mail ?? null,
      userPrincipalName: data.userPrincipalName ?? null,
      jobTitle: data.jobTitle ?? null,
      officeLocation: data.officeLocation ?? null,
      mobilePhone: data.mobilePhone ?? null,
    };
  } catch {
    return null;
  }
}

/**
 * Fetch the signed-in user's Microsoft 365 profile photo as a Blob. Returns
 * null when there is no photo (404) or no token — never throws.
 */
export async function fetchGraphPhotoBlob(signal?: AbortSignal): Promise<Blob | null> {
  const token = await acquireGraphToken();
  if (!token) return null;
  try {
    const res = await fetch(`${GRAPH_BASE}/me/photo/$value`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    if (!res.ok) return null;
    return await res.blob();
  } catch {
    return null;
  }
}
